import flnLoginBg from '@/Assets/Images/fln-login-bg.png?url';
import missionDefaultBg from '@/Assets/Images/level-completion.svg?url';
import missionFlnBg from '@/Assets/Images/level-screen-fln.svg?url';
import stageDefaultBg from '@/Assets/Images/stage-background.png?url';
import stageFlnBg from '@/Assets/Images/stage-fln-bg.svg?url';
import classicLoginBg from '@/Assets/Images/student-login-bg.svg?url';
import subjectFlnBg from '@/Assets/Images/subject-screen-fln.svg?url';

export const THEME_NAMES = ['default', 'fln'] as const;
export type ThemeName = (typeof THEME_NAMES)[number];

/** Coerce an arbitrary value (e.g. a backend-supplied theme name) to a known `ThemeName`, else `default`. */
export const toThemeName = (value: unknown): ThemeName =>
  typeof value === 'string' && (THEME_NAMES as readonly string[]).includes(value)
    ? (value as ThemeName)
    : 'default';

/**
 * The initial theme, derived from the program's persisted `isFln` flag (stored
 * in `adminDetails` at program login via `validate-center`). Seeds the theme
 * before first paint and across reloads; `setCurrentTheme` switches it at
 * runtime the moment a program logs in.
 */
export const resolveInitialTheme = (): ThemeName => {
  try {
    const stored = localStorage.getItem('adminDetails');
    const adminDetails = stored ? (JSON.parse(stored) as { isFln?: boolean }) : undefined;
    return adminDetails?.isFln ? 'fln' : 'default';
  } catch {
    return 'default';
  }
};

export interface ThemeConfig {
  assets: {
    /** Full-screen background per selection screen, swapped by theme. */
    loginBackground: string;
    subjectBackground: string;
    stageBackground: string;
    missionBackground: string;
  };
  typography: {
    headingClass: string;
    headingMargin: string;
  };
  geometry: {
    /** Card corner radius as a Tailwind class (e.g. `rounded-[1.3125rem]`). */
    cardRadius: string;
    /** Card drop-shadow class; `''` (no filter) for `default`. */
    cardShadow: string;
  };
  layout: {
    showStepper: boolean;
    showTestInstructions: boolean;
    welcome: 'splash' | 'fln';
    nameSelection: 'default' | 'fln';
    header: 'default' | 'fln';
    taskPlayer: 'default' | 'fln';
  };
}

export const THEMES: Record<ThemeName, ThemeConfig> = {
  default: {
    assets: {
      loginBackground: classicLoginBg,
      subjectBackground: classicLoginBg,
      stageBackground: stageDefaultBg,
      missionBackground: missionDefaultBg,
    },
    typography: {
      headingClass:
        'text-[3.125rem] font-outline-2 word-spacing-3 font-dynapuff font-bold tracking-[-0.06em]',
      headingMargin: 'mt-3',
    },
    geometry: { cardRadius: 'rounded-[1.3125rem]', cardShadow: '' },
    layout: {
      showStepper: true,
      showTestInstructions: true,
      welcome: 'splash',
      nameSelection: 'default',
      header: 'default',
      taskPlayer: 'default',
    },
  },
  fln: {
    assets: {
      loginBackground: flnLoginBg,
      subjectBackground: subjectFlnBg,
      stageBackground: stageFlnBg,
      missionBackground: missionFlnBg,
    },
    typography: {
      headingClass: 'text-[4rem] font-outline-2 word-spacing-3 font-lilita leading-[1.1]',
      headingMargin: 'mt-[4.1875rem]',
    },
    geometry: {
      cardRadius: 'rounded-[2.0625rem]',
      cardShadow: 'drop-shadow-[0rem_0.412rem_0.206rem_rgba(0,0,0,0.25)]',
    },
    layout: {
      showStepper: false,
      showTestInstructions: false,
      welcome: 'fln',
      nameSelection: 'fln',
      header: 'fln',
      taskPlayer: 'fln',
    },
  },
};
